import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { formatApiError } from "../lib/api";
import { Loader2 } from "lucide-react";

export default function Login() {
    const { login } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await login(email, password);
            nav("/portal");
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            {/* Left brand panel */}
            <div
                className="hidden lg:flex flex-col justify-between p-12 text-white relative overflow-hidden"
                style={{ background: "linear-gradient(180deg, hsl(220 90% 20%) 0%, hsl(222 30% 8%) 100%)" }}
            >
                <div className="absolute inset-0 opacity-[0.06]" style={{
                    backgroundImage: "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
                    backgroundSize: "48px 48px",
                }} />
                <div className="relative z-10 flex flex-col gap-4">
                    <div className="inline-flex items-center bg-white rounded-xl px-6 py-4 shadow-lg self-start">
                        <img
                            src="/brand/eyes-celik-logo-trimmed.webp"
                            alt="Eyes Çelik"
                            className="h-14 w-auto block"
                            data-testid="login-brand-logo"
                        />
                    </div>
                    <div className="text-[11px] uppercase tracking-widest font-medium" style={{ color: "hsl(174 72% 60%)" }}>
                        SATIN ALMA YÖNETİM SİSTEMİ
                    </div>
                </div>
                <div className="relative z-10 space-y-6">
                    <h2 className="text-4xl font-semibold leading-tight max-w-md">
                        Satın alma sürecinizi<br />uçtan uca yönetin.
                    </h2>
                    <p className="text-white/70 max-w-md text-[15px] leading-relaxed">
                        Talep, teklif, sipariş, mal kabul ve fatura süreçlerini
                        tek platformdan yönetin.
                    </p>
                    <div className="grid grid-cols-2 gap-4 max-w-md pt-4">
                        {[
                            ["Talep → Sipariş", "Otomatik akış"],
                            ["3'lü Eşleştirme", "PO • Mal Kabul • Fatura"],
                            ["Teklif Karşılaştırma", "Excel benzeri"],
                            ["Proje Bütçe", "Gerçek zamanlı"],
                        ].map(([k, v]) => (
                            <div key={k} className="border-l-2 pl-3" style={{ borderColor: "hsl(174 72% 45%)" }}>
                                <div className="text-white font-medium text-sm">{k}</div>
                                <div className="text-white/60 text-xs">{v}</div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="relative z-10 text-xs text-white/40">© 2026 Eyes Çelik</div>
            </div>

            {/* Right form */}
            <div className="flex items-center justify-center p-6 md:p-12">
                <div className="w-full max-w-md">
                    <div className="mb-8">
                        <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">
                            Giriş
                        </div>
                        <h1 className="text-3xl font-semibold tracking-tight">Hesabınıza giriş yapın</h1>
                        <p className="text-muted-foreground mt-2 text-sm">
                            Devam etmek için kimlik bilgilerinizi girin.
                        </p>
                    </div>

                    <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-xs uppercase tracking-wider text-muted-foreground">
                                E-posta
                            </Label>
                            <Input
                                id="email"
                                data-testid="login-email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="h-11"
                                autoComplete="email"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground">
                                Şifre
                            </Label>
                            <Input
                                id="password"
                                data-testid="login-password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                className="h-11"
                                autoComplete="current-password"
                            />
                        </div>
                        {error && (
                            <div data-testid="login-error" className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2">
                                {error}
                            </div>
                        )}
                        <Button
                            data-testid="login-submit"
                            type="submit"
                            className="w-full h-11 text-sm font-medium"
                            disabled={loading}
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
                        </Button>
                    </form>
                </div>
            </div>
        </div>
    );
}
